/**
 * Bidirectional Cyrillic ⇄ Latin transliteration for search (`spec.md` §11.9 step 9).
 *
 * The audience is Russian-speaking and the catalogue is Jamaican, so a title is very
 * often mixed-script — `Сизла — Praise Ye Jah`, `Вайбз Картел feat. Popcaan`. Mixed is
 * therefore the *normal* case here, not an edge case, and direction is chosen **per
 * word** by `hasCyrillic`: a Cyrillic word is romanised, a Latin word is cyrillicised,
 * and anything else (digits, emoji, punctuation) is passed through untouched.
 *
 * ## What this is and is not
 *
 * This is a *search-recall* transliterator, not a scholarly romanisation. It deliberately
 * mixes strict transliteration (`ш→sh`, `щ→shch`) with English/Russian phonetic habits
 * (`ee→и`, `oo→у`, `j→дж`, `x→кс` — all four are mandated by `plan.md` T04 and none of
 * them is a letter-for-letter mapping). Two structural consequences:
 *
 *   - **It is lossy and not a bijection.** `и` and `ы` both romanise to `y`/`i`-ish
 *     shapes, `е`/`э` both go to `e`, `ь`/`ъ` vanish. Round-tripping a word through both
 *     directions does not have to return the original, and the golden table in
 *     `__tests__/translit.test.ts` asserts an explicit **85 % floor** rather than
 *     perfection.
 *   - **Phonetic renderings are outside its reach.** `Vybz Kartel → Вайбз Картел` and
 *     `Buju Banton → Буджу Бэнтон` are transcriptions of English *sound*, not of English
 *     *letters*; no letter table produces `ай` from `y` or `э` from `a`. That is what the
 *     hand-curated alias seed (`spec.md` §11.9 step 10) is for, and what
 *     `buildSearchKey` mitigates by indexing **both** scripts side by side, so the exact
 *     spelling as posted always matches even when the algorithm's guess does not.
 *
 * Output is always lowercase: every caller (the search key builder, the FTS5 query
 * builder) feeds it `normalizeText` output and compares against `normalizeText` output.
 */
import { normalizeText } from './normalize.js';
/* ────────────────────────────────────────────────────────────────────────────
 * Direction detection
 * ──────────────────────────────────────────────────────────────────────────── */
const CYRILLIC_RE = /\p{Script=Cyrillic}/u;
const LATIN_RE = /\p{Script=Latin}/u;
/** True when the string contains at least one Cyrillic letter. Chooses the direction. */
export function hasCyrillic(input) {
    return CYRILLIC_RE.test(input);
}
/** True when the string contains at least one Latin letter. */
export function hasLatin(input) {
    return LATIN_RE.test(input);
}
const LATIN_VOWELS = 'aeiouy';
function rulePriority(rule) {
    return rule.priority ?? rule.from.length;
}
function sortRules(rules) {
    // Descending priority. "Digraphs first" is enforced mechanically here instead of
    // relying on the author keeping the literal table order right.
    return [...rules].sort((a, b) => rulePriority(b) - rulePriority(a));
}
function ruleFits(rule, word, offset, end) {
    switch (rule.at) {
        case 'initial':
            if (offset !== 0)
                return false;
            break;
        case 'final':
            if (end !== word.length)
                return false;
            break;
        case 'medial':
            if (end === word.length)
                return false;
            break;
        default:
            break;
    }
    if (rule.after !== undefined) {
        if (offset === 0)
            return false;
        const previous = word.charAt(offset - 1);
        const isVowel = LATIN_VOWELS.includes(previous);
        if (rule.after === 'vowel' ? !isVowel : isVowel)
            return false;
    }
    return true;
}
function applyRules(word, rules) {
    let out = '';
    let i = 0;
    const n = word.length;
    outer: while (i < n) {
        for (const rule of rules) {
            const end = i + rule.from.length;
            if (end > n)
                continue;
            if (!ruleFits(rule, word, i, end))
                continue;
            if (word.startsWith(rule.from, i)) {
                out += rule.to;
                i = end;
                continue outer;
            }
        }
        // No rule matched: keep the character verbatim (digits, and any letter outside both
        // tables — a Greek or Arabic stray must survive rather than be dropped).
        out += word.charAt(i);
        i += 1;
    }
    return out;
}
/* ────────────────────────────────────────────────────────────────────────────
 * Cyrillic → Latin
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Digraph clusters first (`plan.md` T04): `щ→shch`, `ш→sh`, `ч→ch`, `ц→ts`, `ю→yu`,
 * `я→ya`, `ж→zh`, `х→kh`. `дж→j` and `кс→x` are the mirrors of the Latin→Cyrillic
 * `j→дж` / `x→кс` rules, and they are what makes `Буджу → buju` and `Макс → max` work
 * at all.
 */
const RU_TO_LAT = sortRules([
    { from: 'дж', to: 'j' },
    { from: 'кс', to: 'x' },
    { from: 'щ', to: 'shch' },
    { from: 'ш', to: 'sh' },
    { from: 'ч', to: 'ch' },
    { from: 'ц', to: 'ts' },
    { from: 'ю', to: 'yu' },
    { from: 'я', to: 'ya' },
    { from: 'ж', to: 'zh' },
    { from: 'х', to: 'kh' },
    { from: 'ё', to: 'e' },
    { from: 'а', to: 'a' },
    { from: 'б', to: 'b' },
    { from: 'в', to: 'v' },
    { from: 'г', to: 'g' },
    { from: 'д', to: 'd' },
    { from: 'е', to: 'e' },
    { from: 'з', to: 'z' },
    { from: 'и', to: 'i' },
    { from: 'й', to: 'y' },
    { from: 'к', to: 'k' },
    { from: 'л', to: 'l' },
    { from: 'м', to: 'm' },
    { from: 'н', to: 'n' },
    { from: 'о', to: 'o' },
    { from: 'п', to: 'p' },
    { from: 'р', to: 'r' },
    { from: 'с', to: 's' },
    { from: 'т', to: 't' },
    { from: 'у', to: 'u' },
    { from: 'ф', to: 'f' },
    { from: 'ы', to: 'y' },
    { from: 'э', to: 'e' },
    { from: 'ъ', to: '' },
    { from: 'ь', to: '' },
    // Ukrainian / Belarusian letters turn up in reposted metadata; mapping them costs
    // nothing and stops a whole word from surviving as un-transliterated Cyrillic.
    { from: 'і', to: 'i' },
    { from: 'ї', to: 'i' },
    { from: 'є', to: 'e' },
    { from: 'ґ', to: 'g' },
    { from: 'ў', to: 'u' },
]);
/** Romanise one all-Cyrillic word. */
export function translitRuToLat(input) {
    return mapWords(input, (word) => applyRules(word.toLowerCase(), RU_TO_LAT));
}
/* ────────────────────────────────────────────────────────────────────────────
 * Latin → Cyrillic
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Latin→Cyrillic. The four mappings `plan.md` T04 names explicitly — `ee→и`, `oo→у`,
 * `j→дж`, `x→кс` — set the register: this table encodes how a Russian speaker *writes*
 * an English name, not how a linguist transliterates it. The word-final `-ine`/`-ice`/…
 * group is the same idea applied to English's silent `e` (`Alkaline → Алкалайн`,
 * `Spice → Спайс`); `ts` is restricted to `medial` so `Roots` lands on `рутс` and not
 * `руц`.
 */
const LAT_TO_RU = sortRules([
    { from: 'shch', to: 'щ' },
    { from: 'sch', to: 'щ' },
    { from: 'tch', to: 'ч' },
    // `ch` is /k/ in Greek-derived spellings: `Christopher → кристофер`.
    { from: 'chr', to: 'кр' },
    // Scots/Irish `Mc-`: `McGregor → макгрегор`.
    { from: 'mc', to: 'мак', at: 'initial' },
    // Soft `g` at the head of a word: `General → дженерал`.
    { from: 'gen', to: 'джен', at: 'initial' },
    { from: 'cia', to: 'ча', at: 'medial' },
    { from: 'ail', to: 'ейл', at: 'final' },
    // English silent-`e` finals: the vowel in front of them is long.
    { from: 'ine', to: 'айн', at: 'final' },
    { from: 'ice', to: 'айс', at: 'final' },
    { from: 'ise', to: 'айз', at: 'final' },
    { from: 'ike', to: 'айк', at: 'final' },
    { from: 'ite', to: 'айт', at: 'final' },
    { from: 'ide', to: 'айд', at: 'final' },
    { from: 'ife', to: 'айф', at: 'final' },
    { from: 'ime', to: 'айм', at: 'final' },
    { from: 'ire', to: 'айр', at: 'final' },
    { from: 'age', to: 'едж', at: 'final' },
    { from: 'own', to: 'аун' },
    { from: 'ia', to: 'ия', at: 'final' },
    // The silent final `e` of `Bugle`, `Prince`, `Turbulence`, `Boothe`. Priority 2.5 so
    // it outranks every two-character rule (`ce`, `ie`, …) but still loses to the
    // three-character finals above.
    { from: 'e', to: '', at: 'final', after: 'consonant', priority: 2.5 },
    { from: 'ce', to: 'с', at: 'final' },
    { from: 'ch', to: 'ч' },
    { from: 'sh', to: 'ш' },
    { from: 'zh', to: 'ж' },
    { from: 'kh', to: 'х' },
    { from: 'ph', to: 'ф' },
    { from: 'th', to: 'т' },
    { from: 'gh', to: 'г' },
    { from: 'wh', to: 'в' },
    { from: 'ck', to: 'к' },
    // Russian simplifies exactly these two doubles rather than mirroring them:
    // `Sizzla → сизла`, `Lexxus → лексус`. Every other doubled consonant is kept,
    // because Russian keeps it too (`Killer → киллер`, `Perry → перри`).
    { from: 'zz', to: 'з' },
    { from: 'xx', to: 'кс' },
    // Silent `h`: `Johnny → джонни`.
    { from: 'hn', to: 'н' },
    { from: 'qu', to: 'кв' },
    { from: 'ts', to: 'ц', at: 'medial' },
    { from: 'ya', to: 'я' },
    { from: 'yu', to: 'ю' },
    { from: 'yo', to: 'йо' },
    { from: 'ye', to: 'йе', at: 'initial' },
    { from: 'ye', to: 'е' },
    { from: 'ee', to: 'и' },
    { from: 'oo', to: 'у' },
    { from: 'ea', to: 'и' },
    { from: 'ou', to: 'ау' },
    { from: 'ow', to: 'оу' },
    { from: 'ai', to: 'ай' },
    { from: 'ay', to: 'эй', at: 'initial' },
    { from: 'ay', to: 'ей' },
    { from: 'ey', to: 'и', at: 'final' },
    { from: 'ey', to: 'ей' },
    { from: 'oy', to: 'ой' },
    { from: 'oi', to: 'ой' },
    { from: 'ei', to: 'и' },
    { from: 'aw', to: 'о' },
    { from: 'oa', to: 'о', at: 'medial' },
    { from: 'ie', to: 'и' },
    { from: 'oe', to: 'о' },
    { from: 'ci', to: 'си' },
    { from: 'cy', to: 'си' },
    { from: 'ce', to: 'се' },
    // English `h` is silent after a vowel: `Jah → джа`, `Mojah → моджа`.
    { from: 'h', to: '', at: 'final', after: 'vowel' },
    { from: 'y', to: 'й', at: 'initial' },
    { from: 'y', to: 'и' },
    { from: 'a', to: 'а' },
    { from: 'b', to: 'б' },
    { from: 'c', to: 'к' },
    { from: 'd', to: 'д' },
    { from: 'e', to: 'е' },
    { from: 'f', to: 'ф' },
    { from: 'g', to: 'г' },
    { from: 'h', to: 'х' },
    { from: 'i', to: 'и' },
    { from: 'j', to: 'дж' },
    { from: 'k', to: 'к' },
    { from: 'l', to: 'л' },
    { from: 'm', to: 'м' },
    { from: 'n', to: 'н' },
    { from: 'o', to: 'о' },
    { from: 'p', to: 'п' },
    { from: 'q', to: 'к' },
    { from: 'r', to: 'р' },
    { from: 's', to: 'с' },
    { from: 't', to: 'т' },
    { from: 'u', to: 'у' },
    { from: 'v', to: 'в' },
    { from: 'w', to: 'в' },
    { from: 'x', to: 'кс' },
    { from: 'z', to: 'з' },
]);
/** Cyrillicise one all-Latin word. */
export function translitLatToRu(input) {
    return mapWords(input, (word) => applyRules(word.toLowerCase(), LAT_TO_RU));
}
/* ────────────────────────────────────────────────────────────────────────────
 * Word walking
 * ──────────────────────────────────────────────────────────────────────────── */
const WORD_RE = /[\p{L}\p{N}]+/gu;
/**
 * Apply `fn` to every letter/digit run and leave everything between the runs exactly as
 * it was. Word granularity is what makes the position-sensitive rules (`initial`,
 * `final`) mean anything, and what lets a mixed-script string be handled correctly.
 */
function mapWords(input, fn) {
    return input.replace(WORD_RE, fn);
}
/**
 * Transliterate a string, choosing the direction **per word**:
 * Cyrillic words are romanised, Latin words are cyrillicised, everything else is left
 * alone. Output is lowercase.
 *
 * ```ts
 * translit('Сизла')                  // 'sizla'
 * translit('Sizzla')                 // 'сизла'
 * translit('Сизла — Praise Ye Jah')  // 'sizla — прэйз йе джах'
 * ```
 */
export function translit(input) {
    return mapWords(input, (word) => {
        if (hasCyrillic(word))
            return applyRules(word.toLowerCase(), RU_TO_LAT);
        if (hasLatin(word)) {
            return applyRules(word.toLowerCase(), LAT_TO_RU);
        }
        return word.toLowerCase();
    });
}
/* ────────────────────────────────────────────────────────────────────────────
 * Search keys
 * ──────────────────────────────────────────────────────────────────────────── */
/** De-duplicate while preserving first-seen order. */
export function uniqueTokens(tokens) {
    const seen = new Set();
    const out = [];
    for (const token of tokens) {
        if (token === '' || seen.has(token))
            continue;
        seen.add(token);
        out.push(token);
    }
    return out;
}
/**
 * Build the value stored in `tracks.search_key` (`spec.md` §11.9 step 9): the union of
 * the normalised tokens and their transliterations, de-duplicated, order preserved,
 * space-joined.
 *
 * Indexing **both** scripts is the whole point — it is why a user who types the Latin
 * spelling finds a Cyrillic-tagged post and vice versa, and it is the safety net under
 * the transliterator's admitted 15 % miss rate.
 *
 * `null` / `undefined` / empty parts are skipped, so callers can pass optional metadata
 * fields straight through: `buildSearchKey(title, performer, album, fileName, caption)`.
 */
export function buildSearchKey(...parts) {
    const joined = parts
        .filter((part) => typeof part === 'string' && part !== '')
        .join(' ');
    const normalized = normalizeText(joined);
    if (normalized === '')
        return '';
    const transliterated = normalizeText(translit(normalized));
    return uniqueTokens([...normalized.split(' '), ...transliterated.split(' ')]).join(' ');
}
//# sourceMappingURL=translit.js.map